export class ImageResizeError extends Error {
    constructor() {
        super('Could not resize the image to be smaller than 500KiB.');
    }
}

export interface ImageEncoder {
    reduceQuality: (file: File, quality: number) => Promise<File>;
    halve: (file: File) => Promise<File>;
}

const MAX_SIZE_KIB = 500;
const MIN_SIZE_KIB = 437.5;
const START_QUALITY = 0.6;
const MIN_QUALITY_STEP = 0.05;
const MAX_ENCODES = 10;

const sizeInKiB = (file: File) => file.size / 1024;

const inTargetSizeRange = (file: File) => sizeInKiB(file) < MAX_SIZE_KIB && sizeInKiB(file) > MIN_SIZE_KIB;

export const resizeImage = async (originalFile: File, encoder: ImageEncoder): Promise<File> => {
    let encodes = 0;
    const candidates: File[] = [];

    const encode = (file: File, quality: number) => {
        encodes++;

        return encoder.reduceQuality(file, quality);
    };

    const searchQuality = async (file: File) => {
        let lowQuality = START_QUALITY;
        let highQuality = 1;
        let candidate = await encode(file, lowQuality);

        if (sizeInKiB(candidate) >= MAX_SIZE_KIB) {
            return false;
        }
        candidates.push(candidate);

        while (!inTargetSizeRange(candidate) && highQuality - lowQuality > MIN_QUALITY_STEP && encodes < MAX_ENCODES) {
            const quality = (lowQuality + highQuality) / 2;
            const next = await encode(file, quality);

            if (sizeInKiB(next) >= MAX_SIZE_KIB) {
                highQuality = quality;
            } else {
                lowQuality = quality;
                candidate = next;
                candidates.push(next);
            }
        }

        return true;
    };

    let file = originalFile;
    while (!(await searchQuality(file)) && encodes < MAX_ENCODES) {
        file = await encoder.halve(file);
    }

    const [largest] = candidates.sort((a, b) => b.size - a.size);

    if (undefined === largest) {
        throw new ImageResizeError();
    }

    return largest;
};
